import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Download, ChevronDown } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Document, Packer, Paragraph, TextRun, AlignmentType, HeadingLevel } from 'docx';

interface QuitClaimDeedData {
  dealId?: string;
  grantorName: string;
  grantorAddress: string;
  granteeName: string;
  granteeAddress: string;
  municipalityType: string;
  township: string;
  county: string;
  state: string;
  legalDescription: string;
  commonAddress: string;
  parcelNumber: string;
  transferAmount: string;
  recordedDocumentType: string;
  landContractDate: string;
  referenceType: string;
  landContractLiber: string;
  landContractPage: string;
  landContractDocumentNumber: string;
  buyerName: string;
  deedDate: string;
  notaryName: string;
  notaryCounty: string;
  notaryExpiration: string;
}

export function QuitClaimDeedGenerator() {
  const { toast } = useToast();
  const [deals, setDeals] = useState<any[]>([]);
  const [formData, setFormData] = useState<QuitClaimDeedData>(() => {
    const saved = localStorage.getItem('quitClaimDeedProgress');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Error loading saved form data:', e);
      }
    }
    return {
      grantorName: "",
      grantorAddress: "",
      granteeName: "",
      granteeAddress: "",
      municipalityType: "Township",
      township: "",
      county: "",
      state: "Michigan",
      legalDescription: "",
      commonAddress: "",
      parcelNumber: "",
      transferAmount: "Less than $100.00",
      recordedDocumentType: "Land Contract",
      landContractDate: "",
      referenceType: "liber-page",
      landContractLiber: "",
      landContractPage: "",
      landContractDocumentNumber: "",
      buyerName: "",
      deedDate: new Date().toISOString().split('T')[0],
      notaryName: "",
      notaryCounty: "",
      notaryExpiration: "",
    };
  });
  const [isProcessingPdf, setIsProcessingPdf] = useState(false);
  const [extractedPdfText, setExtractedPdfText] = useState<string>("");
  const [parseText, setParseText] = useState('');
  const [isParseOpen, setIsParseOpen] = useState(false);

  useEffect(() => {
    loadDeals();
  }, []);

  useEffect(() => {
    localStorage.setItem('quitClaimDeedProgress', JSON.stringify(formData));
  }, [formData]);

  const loadDeals = async () => {
    const { data, error } = await supabase
      .from('deals')
      .select('*')
      .order('created_at', { ascending: false });
    
    if (error) {
      console.error('Error loading deals:', error);
      return;
    }
    setDeals(data || []);
  };

  const loadDealData = async (dealId: string) => {
    const deal = deals.find(d => d.id === dealId);
    if (!deal) return;

    setFormData({
      ...formData,
      dealId,
      grantorName: deal.seller_name || "",
      grantorAddress: deal.seller_address || "",
      buyerName: deal.buyer_name || "",
      commonAddress: deal.property_address || "",
    });
  };

  const handlePdfUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || file.type !== 'application/pdf') {
      toast({
        title: "Invalid file",
        description: "Please upload a PDF file",
        variant: "destructive",
      });
      return;
    }

    setIsProcessingPdf(true);
    toast({
      title: "Processing PDF",
      description: "Extracting text from document...",
    });
    
    try {
      // Import pdfjs-dist dynamically
      const pdfjsLib = await import('pdfjs-dist');
      pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
        'pdfjs-dist/build/pdf.worker.min.mjs',
        import.meta.url
      ).toString();

      // Read file as ArrayBuffer
      const arrayBuffer = await file.arrayBuffer();
      
      // Load PDF
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      
      let fullText = "";
      
      // Extract text from all pages
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        const pageText = textContent.items
          .map((item: any) => {
            // Ensure we get the string value and handle spacing
            return item.str;
          })
          .filter((text: string) => text.trim().length > 0) // Filter empty strings
          .join(' '); // Join with spaces for readability
        
        if (pageText.trim()) {
          fullText += pageText + "\n\n";
        }
      }

      console.log('Client-side PDF extraction - text length:', fullText.length);
      console.log('First 500 chars:', fullText.substring(0, 500));

      // Store the full text for manual copying
      setExtractedPdfText(fullText);
      
      // Check if we got meaningful text
      if (!fullText || fullText.trim().length < 50) {
        toast({
          title: "Limited text extracted",
          description: "PDF may be image-based. Try using OCR software to convert it first, then paste the text directly into the parser.",
          variant: "destructive",
        });
        setExtractedPdfText(""); // Clear empty text
        return;
      }
      
      // Try to auto-extract fields with improved patterns
      
      // Legal description - starts with "Land situated" or similar, ends before "Commonly known"
      const legalDescMatch = fullText.match(/Land situated in[^]*?(?=Commonly known|Parcel ID|This property may)/i) ||
                            fullText.match(/LEGAL DESCRIPTION[:\s]+([\s\S]*?)(?=COMMONLY KNOWN|COMMON ADDRESS|PARCEL|$)/i);
      
      // Parcel number
      const parcelMatch = fullText.match(/Parcel ID No[.\s:]+([0-9\-]+)/i) ||
                         fullText.match(/PARCEL\s*(?:NUMBER|NO\.?|#)?[:\s]*([A-Z0-9\-]+)/i);
      
      // Common address - after "Commonly known as:"
      const addressMatch = fullText.match(/Commonly known as:\s*([^\n]+)/i) ||
                          fullText.match(/(?:COMMON ADDRESS|PROPERTY ADDRESS)[:\s]+([^\n]+)/i);
      
      // Document recording info - look for Liber and Page in recorded plat references
      const liberMatch = fullText.match(/Liber\s+(\d+)\s+of\s+Deeds/i) ||
                        fullText.match(/LIBER[:\s]*(\d+)/i);
      const pageMatch = fullText.match(/Page\s+(\d+)[,\s]+[A-Za-z\s]+County/i) ||
                       fullText.match(/PAGE[:\s]*(\d+)/i);
      
      // Direct document number if present
      const docNumberMatch = fullText.match(/(?:DOCUMENT|DOC|INSTRUMENT)\s*(?:NUMBER|NO\.?|#)?[:\s]*([A-Z0-9\-]+)/i);
      
      // Extract county and township from legal description
      const countyMatch = fullText.match(/County of\s+([A-Za-z\s]+),\s+State/i) ||
                         fullText.match(/([A-Za-z]+)\s+County/i);
      const townshipMatch = fullText.match(/(?:City|Township|Village)\s+of\s+([A-Za-z\s]+)/i);
      
      // Extract grantor (seller) and grantee (buyer) names
      const grantorMatch = fullText.match(/referred to as the "Seller,?" whose address is[^,]*, and ([^,]+),/i) ||
                          fullText.match(/between\s+([^,]+),\s+hereinafter referred to as the "Seller/i);
      const granteeMatch = fullText.match(/referred to as the "Purchaser",? whose address is[^,]*, and ([^,]+)/i) ||
                          fullText.match(/and\s+([^,]+),\s+hereinafter referred to as the "Purchaser/i);
      
      const updates: Partial<QuitClaimDeedData> = {};
      
      if (legalDescMatch) {
        updates.legalDescription = legalDescMatch[0]?.trim() || legalDescMatch[1]?.trim() || '';
      }
      
      if (parcelMatch) {
        updates.parcelNumber = parcelMatch[1].trim();
      }
      
      if (addressMatch) {
        updates.commonAddress = addressMatch[1].trim();
      }
      
      if (docNumberMatch) {
        updates.landContractDocumentNumber = docNumberMatch[1].trim();
      }
      
      if (liberMatch) {
        updates.landContractLiber = liberMatch[1].trim();
      }
      
      if (pageMatch) {
        updates.landContractPage = pageMatch[1].trim();
      }
      
      if (countyMatch) {
        updates.county = countyMatch[1].trim();
      }
      
      if (townshipMatch) {
        updates.township = townshipMatch[1].trim();
      }
      
      if (grantorMatch) {
        const grantorName = grantorMatch[1]?.trim();
        if (grantorName) {
          updates.grantorName = grantorName;
        }
      }
      
      if (granteeMatch) {
        const granteeName = granteeMatch[1]?.trim();
        if (granteeName) {
          updates.granteeName = granteeName;
        }
      }
      
      if (Object.keys(updates).length > 0) {
        setFormData(prev => ({
          ...prev,
          ...updates
        }));
        
        const extractedFields = Object.keys(updates).join(', ');
        toast({
          title: "PDF data extracted",
          description: `Auto-filled: ${extractedFields}`,
        });
      } else {
        toast({
          title: "PDF processed",
          description: "No fields could be auto-extracted. You can copy from the extracted text below.",
        });
      }

    } catch (error) {
      console.error('PDF processing error:', error);
      toast({
        title: "Error processing PDF",
        description: "Could not parse PDF. Please copy text manually.",
        variant: "destructive",
      });
    } finally {
      setIsProcessingPdf(false);
      event.target.value = '';
    }
  };

  const clearForm = () => {
    const emptyForm: QuitClaimDeedData = {
      grantorName: "",
      grantorAddress: "",
      granteeName: "",
      granteeAddress: "",
      municipalityType: "Township",
      township: "",
      county: "",
      state: "Michigan",
      legalDescription: "",
      commonAddress: "",
      parcelNumber: "",
      transferAmount: "Less than $100.00",
      recordedDocumentType: "Land Contract",
      landContractDate: "",
      referenceType: "liber-page",
      landContractLiber: "",
      landContractPage: "",
      landContractDocumentNumber: "",
      buyerName: "",
      deedDate: new Date().toISOString().split('T')[0],
      notaryName: "",
      notaryCounty: "",
      notaryExpiration: "",
    };
    setFormData(emptyForm);
    localStorage.removeItem('quitClaimDeedProgress');
    toast({
      title: "Form Cleared",
      description: "All fields have been reset",
    });
  };

  const extractLegalDescription = () => {
    if (!parseText.trim()) {
      toast({
        title: "No text to parse",
        description: "Please paste some text first",
        variant: "destructive",
      });
      return;
    }

    const patterns = [
      /(?:described as|legal description|legally described as)[:\s]+(.+?)(?:commonly known|parcel|tax|$)/is,
      /(?:SITUATED IN|Located in|being in)[:\s]+(.+?)(?:commonly known|parcel|tax|$)/is,
      /((?:Section|Sec\.|Township|Twp\.|Range|Rng\.)[\s\S]+?)(?:commonly known|parcel|tax|also known|$)/is,
      /(Lot[s]?\s+\d+[\s\S]+?(?:Subdivision|Addition|Plat)[\s\S]+?)(?:commonly known|parcel|tax|also known|$)/is,
      /((?:.*(?:situated|located|being|township|range|section|lot|block|subdivision|metes|bounds|chains|rods|degrees).*)+)/is
    ];

    let extracted = '';
    for (const pattern of patterns) {
      const match = parseText.match(pattern);
      if (match && match[1] && match[1].trim().length > 20) {
        extracted = match[1].trim();
        break;
      }
    }

    if (extracted) {
      extracted = extracted.replace(/\s+/g, ' ').replace(/\n\s*\n/g, '\n').trim();
      setFormData(prev => ({ ...prev, legalDescription: extracted }));
      toast({
        title: "Success!",
        description: "Legal description extracted and populated",
      });
      setIsParseOpen(false);
    } else {
      toast({
        title: "Could not find legal description",
        description: "Please try copying a larger section or enter manually.",
        variant: "destructive",
      });
    }
  };

  const generateDocument = async () => {
    if (!formData.grantorName || !formData.granteeName || !formData.legalDescription) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields",
        variant: "destructive"
      });
      return;
    }

    try {
      const doc = new Document({
        sections: [{
          properties: {},
          children: [
            new Paragraph({
              text: "Quit Claim Deed",
              heading: HeadingLevel.HEADING_1,
              alignment: AlignmentType.CENTER,
              spacing: { after: 400 }
            }),

            new Paragraph({
              children: [
                new TextRun({ text: "THE GRANTOR(S) ", bold: true }),
                new TextRun(`${formData.grantorName}, whose address is ${formData.grantorAddress}, quit claims to `),
                new TextRun({ text: formData.granteeName, bold: true }),
                new TextRun(`, whose address is ${formData.granteeAddress}, the following described premises:`)
              ],
              spacing: { after: 300 }
            }),

            new Paragraph({
              text: `SITUATED IN THE ${formData.municipalityType.toUpperCase()} OF ${formData.township.toUpperCase()}, COUNTY OF ${formData.county.toUpperCase()}, STATE OF ${formData.state.toUpperCase()}`,
              heading: HeadingLevel.HEADING_2,
              alignment: AlignmentType.CENTER,
              spacing: { before: 200, after: 300 }
            }),

            new Paragraph({
              text: formData.legalDescription,
              spacing: { after: 300 }
            }),

            new Paragraph({
              text: `Commonly known as ${formData.commonAddress}`,
              spacing: { after: 100 }
            }),

            new Paragraph({
              text: `Parcel No. ${formData.parcelNumber}`,
              spacing: { after: 300 }
            }),

            new Paragraph({
              text: "THIS PROPERTY MAY BE LOCATED WITHIN THE VICINITY OF FARMLAND OR FARM OPERATION. GENERALLY ACCEPTED AGRICULTURAL AND MANAGEMENT PRACTICES WHICH MAY GENERATE NOISE, DUST, ODORS AND OTHER ASSOCIATED CONDITIONS MAY BE USED AND ARE PROTECTED BY THE MICHIGAN RIGHT TO FARM ACT. THE GRANTOR GRANTS TO THE GRANTEE THE RIGHT TO MAKE MAXIMUM DIVISIONS UNDER SECTION 108 OF THE LAND DIVISION ACT, ACT NO. 288 OF THE PUBLIC ACTS OF 1967.",
              spacing: { after: 300 }
            }),

            new Paragraph({
              text: `FOR THE SUM OF ${formData.transferAmount}, THIS DEED IS EXEMPT FROM TRANSFER TAX PURSUANT TO MCLA 207.505(A) AND MCLA 207.526(A).`,
              spacing: { after: 300 }
            }),

            new Paragraph({
              text: `Subject to terms, conditions and provisions of the ${formData.recordedDocumentType} between the above Grantor(s) (Seller) and ${formData.buyerName}, as Purchaser(s), as evidenced by ${formData.recordedDocumentType === "Memorandum of Land Contract" ? "Memorandum of Land Contract" : "Land Contract"} recorded on ${formData.landContractDate}, ${formData.referenceType === 'liber-page' ? `in Liber ${formData.landContractLiber}, Page ${formData.landContractPage}` : `as Document Number ${formData.landContractDocumentNumber}`}, in the Register of Deeds Office of ${formData.county} County, Michigan. By this conveyance the Grantor(s) convey all their Fee and vendor's interest in said Land Contract.`,
              spacing: { after: 300 }
            }),

            new Paragraph({
              text: `Dated this ___ day of ${new Date(formData.deedDate).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}.`,
              spacing: { after: 100 }
            }),

            new Paragraph({
              text: "Signed by:",
              spacing: { after: 100 }
            }),

            new Paragraph({
              text: "_______________________________",
              spacing: { before: 200, after: 50 }
            }),

            new Paragraph({
              text: formData.grantorName,
              spacing: { after: 400 }
            }),

            new Paragraph({
              text: "STATE OF MICHIGAN",
              heading: HeadingLevel.HEADING_2,
              alignment: AlignmentType.CENTER,
              spacing: { before: 300, after: 100 }
            }),

            new Paragraph({
              text: `COUNTY OF ${formData.notaryCounty.toUpperCase()}`,
              heading: HeadingLevel.HEADING_2,
              alignment: AlignmentType.CENTER,
              spacing: { after: 200 }
            }),

            new Paragraph({
              text: `The foregoing instrument was acknowledged before me this ___ day of ${new Date(formData.deedDate).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })} by ${formData.grantorName}.`,
              spacing: { after: 300 }
            }),

            new Paragraph({
              text: "_______________________________",
              spacing: { before: 200, after: 50 }
            }),

            new Paragraph({
              text: formData.notaryName,
              spacing: { after: 50 }
            }),

            new Paragraph({
              text: "NOTARY PUBLIC-STATE OF MICHIGAN",
              spacing: { after: 50 }
            }),

            new Paragraph({
              text: `COUNTY OF ${formData.notaryCounty.toUpperCase()}`,
              spacing: { after: 50 }
            }),

            new Paragraph({
              text: `My Commission Expires ${formData.notaryExpiration}`,
              spacing: { after: 300 }
            }),
          ]
        }]
      });

      const blob = await Packer.toBlob(doc);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Quit_Claim_Deed_${formData.granteeName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.docx`;
      link.click();
      window.URL.revokeObjectURL(url);

      localStorage.removeItem('quitClaimDeedProgress');
      toast({
        title: "Success",
        description: "Quit Claim Deed generated successfully. Form progress cleared."
      });
    } catch (error) {
      console.error('Error generating document:', error);
      toast({
        title: "Error",
        description: "Failed to generate document",
        variant: "destructive"
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>Quit Claim Deed Generator</span>
          <Button variant="outline" size="sm" onClick={clearForm}>
            Clear Form
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label>Load from Deal (Optional)</Label>
          <Select onValueChange={loadDealData}>
            <SelectTrigger>
              <SelectValue placeholder="Select a deal..." />
            </SelectTrigger>
            <SelectContent>
              {deals.map(deal => (
                <SelectItem key={deal.id} value={deal.id}>
                  {deal.deal_title} - {deal.buyer_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>Upload Land Contract / Memorandum (Optional)</Label>
          <Input
            type="file"
            accept=".pdf"
            onChange={handlePdfUpload}
            disabled={isProcessingPdf}
          />
          {isProcessingPdf && (
            <p className="text-sm text-muted-foreground">Processing document...</p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Grantor Name (Seller) *</Label>
            <Input
              value={formData.grantorName}
              onChange={(e) => setFormData({ ...formData, grantorName: e.target.value })}
              placeholder="John R. Murray and Shelly J. Murray"
            />
          </div>

          <div className="space-y-2">
            <Label>Grantor Address</Label>
            <Input
              value={formData.grantorAddress}
              onChange={(e) => setFormData({ ...formData, grantorAddress: e.target.value })}
              placeholder="1070 E St Joe Hwy, Mulliken, MI 48861"
            />
          </div>

          <div className="space-y-2">
            <Label>Grantee Name (Investor) *</Label>
            <Input
              value={formData.granteeName}
              onChange={(e) => setFormData({ ...formData, granteeName: e.target.value })}
              placeholder="First National Bank of America"
            />
          </div>

          <div className="space-y-2">
            <Label>Grantee Address</Label>
            <Input
              value={formData.granteeAddress}
              onChange={(e) => setFormData({ ...formData, granteeAddress: e.target.value })}
              placeholder="P.O. Box 980, East Lansing, MI 48823"
            />
          </div>

          <div className="space-y-2">
            <Label>Municipality Type</Label>
            <Select value={formData.municipalityType} onValueChange={(value) => setFormData({ ...formData, municipalityType: value })}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Township">Township</SelectItem>
                <SelectItem value="City">City</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>{formData.municipalityType} Name *</Label>
            <Input
              value={formData.township}
              onChange={(e) => setFormData({ ...formData, township: e.target.value })}
              placeholder="Oneida"
            />
          </div>

          <div className="space-y-2">
            <Label>County *</Label>
            <Input
              value={formData.county}
              onChange={(e) => setFormData({ ...formData, county: e.target.value })}
              placeholder="Eaton"
            />
          </div>

          <div className="space-y-2">
            <Label>Common Address</Label>
            <Input
              value={formData.commonAddress}
              onChange={(e) => setFormData({ ...formData, commonAddress: e.target.value })}
              placeholder="1070 E St Joe Hwy, Grand Ledge, MI 48837"
            />
          </div>

          <div className="space-y-2">
            <Label>Parcel Number</Label>
            <Input
              value={formData.parcelNumber}
              onChange={(e) => setFormData({ ...formData, parcelNumber: e.target.value })}
              placeholder="030-020-100-004-03"
            />
          </div>

          <div className="space-y-2">
            <Label>Buyer Name (from Land Contract)</Label>
            <Input
              value={formData.buyerName}
              onChange={(e) => setFormData({ ...formData, buyerName: e.target.value })}
              placeholder="James A. Nelson"
            />
          </div>

          <div className="space-y-2">
            <Label>Recorded Document Type</Label>
            <Select value={formData.recordedDocumentType} onValueChange={(value) => setFormData({ ...formData, recordedDocumentType: value })}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Land Contract">Land Contract</SelectItem>
                <SelectItem value="Memorandum of Land Contract">Memorandum of Land Contract</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>{formData.recordedDocumentType} Date</Label>
            <Input
              type="date"
              value={formData.landContractDate}
              onChange={(e) => setFormData({ ...formData, landContractDate: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label>Recording Reference Type</Label>
            <Select
              value={formData.referenceType}
              onValueChange={(value) => setFormData({ ...formData, referenceType: value })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="liber-page">Liber and Page</SelectItem>
                <SelectItem value="document-number">Document Number</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {formData.referenceType === 'liber-page' ? (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{formData.recordedDocumentType} Liber</Label>
                <Input
                  value={formData.landContractLiber}
                  onChange={(e) => setFormData({ ...formData, landContractLiber: e.target.value })}
                  placeholder="3126"
                />
              </div>
              <div className="space-y-2">
                <Label>{formData.recordedDocumentType} Page</Label>
                <Input
                  value={formData.landContractPage}
                  onChange={(e) => setFormData({ ...formData, landContractPage: e.target.value })}
                  placeholder="0594"
                />
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <Label>Document Number</Label>
              <Input
                value={formData.landContractDocumentNumber}
                onChange={(e) => setFormData({ ...formData, landContractDocumentNumber: e.target.value })}
                placeholder="e.g., 2024-123456"
              />
            </div>
          )}

          <div className="space-y-2">
            <Label>Notary Name</Label>
            <Input
              value={formData.notaryName}
              onChange={(e) => setFormData({ ...formData, notaryName: e.target.value })}
              placeholder="Annie K. Barr"
            />
          </div>

          <div className="space-y-2">
            <Label>Notary County</Label>
            <Input
              value={formData.notaryCounty}
              onChange={(e) => setFormData({ ...formData, notaryCounty: e.target.value })}
              placeholder="Clinton"
            />
          </div>

          <div className="space-y-2">
            <Label>Notary Expiration</Label>
            <Input
              value={formData.notaryExpiration}
              onChange={(e) => setFormData({ ...formData, notaryExpiration: e.target.value })}
              placeholder="July 9, 2026"
            />
          </div>
        </div>

        {extractedPdfText && (
          <div className="space-y-2 p-4 border rounded-lg bg-muted/50">
            <div className="flex items-center justify-between">
              <Label className="text-base font-semibold">Extracted PDF Text (Copy From Here)</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  navigator.clipboard.writeText(extractedPdfText);
                  toast({
                    title: "Copied to clipboard",
                    description: "PDF text copied successfully",
                  });
                }}
              >
                Copy All Text
              </Button>
            </div>
            <Textarea
              value={extractedPdfText}
              readOnly
              className="font-mono text-xs"
              rows={10}
            />
            <p className="text-xs text-muted-foreground">
              Select and copy the legal description or any other information from the text above
            </p>
          </div>
        )}

        <div className="space-y-2">
          <Label>Legal Description *</Label>
          
          <Collapsible open={isParseOpen} onOpenChange={setIsParseOpen} className="mb-3">
            <CollapsibleTrigger asChild>
              <Button type="button" variant="outline" size="sm" className="w-full justify-between mb-2">
                Parse Legal Description from Text
                <ChevronDown className={`h-4 w-4 transition-transform ${isParseOpen ? 'rotate-180' : ''}`} />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-2 pt-2">
              <p className="text-sm text-muted-foreground">
                Paste text from any document below (Ctrl+V / Cmd+V), or use the button to paste from clipboard.
              </p>
              <div className="flex gap-2 mb-2">
                <Button
                  type="button"
                  onClick={async () => {
                    try {
                      const text = await navigator.clipboard.readText();
                      const cleanedText = text
                        .replace(/[\u200B-\u200D\uFEFF]/g, '') // Remove zero-width characters
                        .replace(/\u00A0/g, ' ') // Replace non-breaking spaces
                        .trim();
                      setParseText(cleanedText);
                      toast({
                        title: "Text pasted from clipboard",
                        description: `${cleanedText.length} characters loaded`,
                      });
                    } catch (err) {
                      toast({
                        title: "Clipboard access denied",
                        description: "Please use Ctrl+V or right-click paste instead",
                        variant: "destructive",
                      });
                    }
                  }}
                  variant="outline"
                  size="sm"
                >
                  Paste from Clipboard
                </Button>
              </div>
              <Textarea
                placeholder="Paste your text here (Ctrl+V or Cmd+V)..."
                value={parseText}
                onChange={(e) => setParseText(e.target.value)}
                rows={8}
                className="font-mono text-xs"
              />
              <div className="flex gap-2">
                <Button
                  type="button"
                  onClick={extractLegalDescription}
                  size="sm"
                  className="flex-1"
                >
                  Extract Legal Description
                </Button>
                <Button
                  type="button"
                  onClick={() => setParseText('')}
                  variant="outline"
                  size="sm"
                >
                  Clear
                </Button>
              </div>
            </CollapsibleContent>
          </Collapsible>

          <Textarea
            value={formData.legalDescription}
            onChange={(e) => setFormData({ ...formData, legalDescription: e.target.value })}
            placeholder="Paste or type the legal description here..."
            rows={6}
          />
        </div>

        <Button onClick={generateDocument} className="w-full">
          <Download className="mr-2 h-4 w-4" />
          Generate Quit Claim Deed
        </Button>
      </CardContent>
    </Card>
  );
}
